import React from "react";
import {
  FieldWrapper,
  FilterSelect,
  FilterMultiSelect,
  FilterToggle,
  FilterSortGroup,
  CompactDateInput,
  formatGroupedValues,
} from "./jobsFilterUtils";

// --- SESSIONS ---
export const SESSIONS_SORT_OPTIONS = [
  { value: "name", label: "Account" },
  { value: "state", label: "Status" },
  { value: "create_date", label: "Created" },
  { value: "expiry_date", label: "Expires" },
];

export function AdminSessionsFilterFields({
  focusedFieldId,
  onFocusClear,
  sortKey,
  setSortKey,
  sortDir,
  setSortDir,
}) {
  return (
    <FieldWrapper
      fieldId="sort"
      label=""
      focusedFieldId={focusedFieldId}
      onFocusClear={onFocusClear}
    >
      <FilterSortGroup
        id="admin-sessions-sort"
        sortBy={sortKey}
        sortOrder={sortDir}
        onSortByChange={setSortKey}
        onSortOrderChange={setSortDir}
        sortOptions={SESSIONS_SORT_OPTIONS}
      />
    </FieldWrapper>
  );
}

export function buildAdminSessionsFilterChips({ sortDir }) {
  const chips = [];
  if (sortDir !== "desc") {
    chips.push({
      id: "sortDir",
      key: "sortDir",
      fieldId: "sort",
      label: `Direction: ${sortDir === "asc" ? "Ascending" : "Descending"}`,
    });
  }
  return chips;
}

// --- SIGN INS ---
export const SIGNINS_SORT_OPTIONS = [
  { value: "name", label: "Account" },
  { value: "state", label: "Status" },
  { value: "signin_date", label: "Date" },
];

export const SIGNINS_STATUS_OPTIONS = [
  { value: "success", label: "Success" },
  { value: "failure", label: "Failure" },
  { value: "reset", label: "Reset" },
  { value: "passwordreset", label: "Password Reset" },
  { value: "verificationrequested", label: "Verification Requested" },
  { value: "verificationfailed", label: "Verification Failed" },
];

export function AdminSigninsFilterFields({
  focusedFieldId,
  onFocusClear,
  fromDate,
  setFromDate,
  toDate,
  setToDate,
  selectedSigninStatuses = [],
  setSelectedSigninStatuses,
  sortKey,
  setSortKey,
  sortDir,
  setSortDir,
}) {
  return (
    <>
      <FieldWrapper
        fieldId="status"
        label="Status"
        focusedFieldId={focusedFieldId}
        onFocusClear={onFocusClear}
      >
        <FilterMultiSelect
          id="admin-signins-status"
          options={SIGNINS_STATUS_OPTIONS}
          value={selectedSigninStatuses}
          onChange={setSelectedSigninStatuses}
          placeholder="Select Status..."
        />
      </FieldWrapper>

      <FieldWrapper
        fieldId="fromDate"
        label="From Date"
        focusedFieldId={focusedFieldId}
        onFocusClear={onFocusClear}
      >
        <CompactDateInput
          id="admin-signins-from"
          value={fromDate}
          onChange={setFromDate}
        />
      </FieldWrapper>

      <FieldWrapper
        fieldId="toDate"
        label="To Date"
        focusedFieldId={focusedFieldId}
        onFocusClear={onFocusClear}
      >
        <CompactDateInput
          id="admin-signins-to"
          value={toDate}
          onChange={setToDate}
        />
      </FieldWrapper>

      <FieldWrapper
        fieldId="sort"
        label=""
        focusedFieldId={focusedFieldId}
        onFocusClear={onFocusClear}
      >
        <FilterSortGroup
          id="admin-signins-sort"
          sortBy={sortKey}
          sortOrder={sortDir}
          onSortByChange={setSortKey}
          onSortOrderChange={setSortDir}
          sortOptions={SIGNINS_SORT_OPTIONS}
        />
      </FieldWrapper>
    </>
  );
}

export function buildAdminSigninsFilterChips({
  fromDate,
  toDate,
  selectedSigninStatuses = [],
  sortDir,
}) {
  const chips = [];
  if (selectedSigninStatuses.length > 0) {
    const formatStatus = (val) => SIGNINS_STATUS_OPTIONS.find((o) => o.value === val)?.label || val;
    chips.push({
      id: "status",
      key: "selectedSigninStatuses",
      fieldId: "status",
      label: `Statuses: ${formatGroupedValues(selectedSigninStatuses, formatStatus, 3)}`,
    });
  }
  if (fromDate) {
    chips.push({
      id: "fromDate",
      key: "fromDate",
      fieldId: "fromDate",
      label: `From: ${fromDate}`,
    });
  }
  if (toDate) {
    chips.push({
      id: "toDate",
      key: "toDate",
      fieldId: "toDate",
      label: `To: ${toDate}`,
    });
  }
  if (sortDir !== "desc") {
    chips.push({
      id: "sortDir",
      key: "sortDir",
      fieldId: "sort",
      label: `Direction: ${sortDir === "asc" ? "Ascending" : "Descending"}`,
    });
  }
  return chips;
}

// --- IDENTIFICATIONS ---
export const IDENTIFICATIONS_SORT_OPTIONS = [
  { value: "name", label: "Account" },
  { value: "client_name", label: "Client" },
  { value: "create_date", label: "Date" },
];

export const DEVICE_TYPE_OPTIONS = [
  { value: "all", label: "All" },
  { value: "personal", label: "Personal" },
  { value: "app", label: "App" },
];

export function AdminIdentificationsFilterFields({
  focusedFieldId,
  onFocusClear,
  deviceTypeFilter,
  setDeviceTypeFilter,
  sortKey,
  setSortKey,
  sortDir,
  setSortDir,
}) {
  return (
    <>
      <FieldWrapper
        fieldId="deviceType"
        label="Device Type"
        focusedFieldId={focusedFieldId}
        onFocusClear={onFocusClear}
      >
        <FilterSelect
          id="admin-ident-devicetype"
          value={deviceTypeFilter}
          onChange={setDeviceTypeFilter}
          options={DEVICE_TYPE_OPTIONS}
          placeholder={null}
        />
      </FieldWrapper>

      <FieldWrapper
        fieldId="sort"
        label=""
        focusedFieldId={focusedFieldId}
        onFocusClear={onFocusClear}
      >
        <FilterSortGroup
          id="admin-ident-sort"
          sortBy={sortKey}
          sortOrder={sortDir}
          onSortByChange={setSortKey}
          onSortOrderChange={setSortDir}
          sortOptions={IDENTIFICATIONS_SORT_OPTIONS}
        />
      </FieldWrapper>
    </>
  );
}

export function buildAdminIdentificationsFilterChips({
  deviceTypeFilter,
  sortDir,
}) {
  const chips = [];
  if (deviceTypeFilter && deviceTypeFilter !== "all") {
    const typeLabel =
      DEVICE_TYPE_OPTIONS.find((o) => o.value === deviceTypeFilter)?.label ||
      deviceTypeFilter;
    chips.push({
      id: "deviceType",
      key: "deviceTypeFilter",
      fieldId: "deviceType",
      label: `Type: ${typeLabel}`,
    });
  }
  if (sortDir !== "desc") {
    chips.push({
      id: "sortDir",
      key: "sortDir",
      fieldId: "sort",
      label: `Direction: ${sortDir === "asc" ? "Ascending" : "Descending"}`,
    });
  }
  return chips;
}

// --- TRUSTED DEVICES ---
export const TRUSTED_SORT_OPTIONS = [
  { value: "client_name", label: "Client" },
  { value: "signin_date", label: "Last Sign in By" },
  { value: "create_date", label: "Created" },
];

export function AdminTrustedDevicesFilterFields({
  focusedFieldId,
  onFocusClear,
  trustedDeviceType,
  setTrustedDeviceType,
  trustedFromDate,
  setTrustedFromDate,
  trustedToDate,
  setTrustedToDate,
  trustedShowDeleted,
  setTrustedShowDeleted,
  sortKey,
  setSortKey,
  sortDir,
  setSortDir,
}) {
  return (
    <>
      <FieldWrapper
        fieldId="deviceType"
        label="Device Type"
        focusedFieldId={focusedFieldId}
        onFocusClear={onFocusClear}
      >
        <FilterSelect
          id="admin-trusted-devicetype"
          value={trustedDeviceType}
          onChange={setTrustedDeviceType}
          options={DEVICE_TYPE_OPTIONS}
          placeholder={null}
        />
      </FieldWrapper>

      <FieldWrapper
        fieldId="fromDate"
        label="From Last Signin Date"
        focusedFieldId={focusedFieldId}
        onFocusClear={onFocusClear}
      >
        <CompactDateInput
          id="admin-trusted-from"
          value={trustedFromDate}
          onChange={setTrustedFromDate}
        />
      </FieldWrapper>

      <FieldWrapper
        fieldId="toDate"
        label="To Last Signin Date"
        focusedFieldId={focusedFieldId}
        onFocusClear={onFocusClear}
      >
        <CompactDateInput
          id="admin-trusted-to"
          value={trustedToDate}
          onChange={setTrustedToDate}
        />
      </FieldWrapper>

      <FieldWrapper
        fieldId="showDeleted"
        label=""
        focusedFieldId={focusedFieldId}
        onFocusClear={onFocusClear}
      >
        <FilterToggle
          id="admin-trusted-deleted"
          label="Show Deleted"
          checked={trustedShowDeleted}
          onChange={setTrustedShowDeleted}
        />
      </FieldWrapper>

      <FieldWrapper
        fieldId="sort"
        label=""
        focusedFieldId={focusedFieldId}
        onFocusClear={onFocusClear}
      >
        <FilterSortGroup
          id="admin-trusted-sort"
          sortBy={sortKey}
          sortOrder={sortDir}
          onSortByChange={setSortKey}
          onSortOrderChange={setSortDir}
          sortOptions={TRUSTED_SORT_OPTIONS}
        />
      </FieldWrapper>
    </>
  );
}

export function buildAdminTrustedDevicesFilterChips({
  trustedDeviceType,
  trustedFromDate,
  trustedToDate,
  trustedShowDeleted,
  sortDir,
}) {
  const chips = [];
  if (trustedDeviceType && trustedDeviceType !== "all") {
    const typeLabel =
      DEVICE_TYPE_OPTIONS.find((o) => o.value === trustedDeviceType)?.label ||
      trustedDeviceType;
    chips.push({
      id: "deviceType",
      key: "trustedDeviceType",
      fieldId: "deviceType",
      label: `Type: ${typeLabel}`,
    });
  }
  if (trustedFromDate) {
    chips.push({
      id: "fromDate",
      key: "trustedFromDate",
      fieldId: "fromDate",
      label: `From: ${trustedFromDate}`,
    });
  }
  if (trustedToDate) {
    chips.push({
      id: "toDate",
      key: "trustedToDate",
      fieldId: "toDate",
      label: `To: ${trustedToDate}`,
    });
  }
  if (trustedShowDeleted) {
    chips.push({
      id: "showDeleted",
      key: "trustedShowDeleted",
      fieldId: "showDeleted",
      label: "Show Deleted",
    });
  }
  if (sortDir !== "desc") {
    chips.push({
      id: "sortDir",
      key: "sortDir",
      fieldId: "sort",
      label: `Direction: ${sortDir === "asc" ? "Ascending" : "Descending"}`,
    });
  }
  return chips;
}

// --- ONE TIME PASSWORDS ---
export const OTPS_SORT_OPTIONS = [
  { value: "state", label: "Status" },
  { value: "create_date", label: "Created" },
  { value: "used_date", label: "Used Date" },
];

export function AdminOtpsFilterFields({
  focusedFieldId,
  onFocusClear,
  otpsFromDate,
  setOtpsFromDate,
  otpsToDate,
  setOtpsToDate,
  sortKey,
  setSortKey,
  sortDir,
  setSortDir,
}) {
  return (
    <>
      <FieldWrapper
        fieldId="fromDate"
        label="From Date"
        focusedFieldId={focusedFieldId}
        onFocusClear={onFocusClear}
      >
        <CompactDateInput
          id="admin-otps-from"
          value={otpsFromDate}
          onChange={setOtpsFromDate}
        />
      </FieldWrapper>

      <FieldWrapper
        fieldId="toDate"
        label="To Date"
        focusedFieldId={focusedFieldId}
        onFocusClear={onFocusClear}
      >
        <CompactDateInput
          id="admin-otps-to"
          value={otpsToDate}
          onChange={setOtpsToDate}
        />
      </FieldWrapper>

      <FieldWrapper
        fieldId="sort"
        label=""
        focusedFieldId={focusedFieldId}
        onFocusClear={onFocusClear}
      >
        <FilterSortGroup
          id="admin-otps-sort"
          sortBy={sortKey}
          sortOrder={sortDir}
          onSortByChange={setSortKey}
          onSortOrderChange={setSortDir}
          sortOptions={OTPS_SORT_OPTIONS}
        />
      </FieldWrapper>
    </>
  );
}

export function buildAdminOtpsFilterChips({
  otpsFromDate,
  otpsToDate,
  sortDir,
}) {
  const chips = [];
  if (otpsFromDate) {
    chips.push({
      id: "fromDate",
      key: "otpsFromDate",
      fieldId: "fromDate",
      label: `From: ${otpsFromDate}`,
    });
  }
  if (otpsToDate) {
    chips.push({
      id: "toDate",
      key: "otpsToDate",
      fieldId: "toDate",
      label: `To: ${otpsToDate}`,
    });
  }
  if (sortDir !== "desc") {
    chips.push({
      id: "sortDir",
      key: "sortDir",
      fieldId: "sort",
      label: `Direction: ${sortDir === "asc" ? "Ascending" : "Descending"}`,
    });
  }
  return chips;
}

// --- INVITES ---
export const INVITES_SORT_OPTIONS = [
  { value: "invitation_creator", label: "Invitation Creator" },
  { value: "status", label: "Status" },
  { value: "created", label: "Created" },
];

export const INVITES_STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "used", label: "Used" },
  { value: "expired", label: "Expired" },
];

export function AdminInvitesFilterFields({
  focusedFieldId,
  onFocusClear,
  invitesStatus,
  setInvitesStatus,
  sortKey,
  setSortKey,
  sortDir,
  setSortDir,
}) {
  return (
    <>
      <FieldWrapper
        fieldId="status"
        label="Status"
        focusedFieldId={focusedFieldId}
        onFocusClear={onFocusClear}
      >
        <FilterSelect
          id="admin-invites-status"
          value={invitesStatus}
          onChange={setInvitesStatus}
          options={INVITES_STATUS_OPTIONS}
          placeholder="All Statuses"
        />
      </FieldWrapper>

      <FieldWrapper
        fieldId="sort"
        label=""
        focusedFieldId={focusedFieldId}
        onFocusClear={onFocusClear}
      >
        <FilterSortGroup
          id="admin-invites-sort"
          sortBy={sortKey}
          sortOrder={sortDir}
          onSortByChange={setSortKey}
          onSortOrderChange={setSortDir}
          sortOptions={INVITES_SORT_OPTIONS}
        />
      </FieldWrapper>
    </>
  );
}

export function buildAdminInvitesFilterChips({
  invitesStatus,
  sortDir,
}) {
  const chips = [];
  if (invitesStatus) {
    chips.push({
      id: "status",
      key: "invitesStatus",
      fieldId: "status",
      label: `Status: ${invitesStatus}`,
    });
  }
  if (sortDir !== "desc") {
    chips.push({
      id: "sortDir",
      key: "sortDir",
      fieldId: "sort",
      label: `Direction: ${sortDir === "asc" ? "Ascending" : "Descending"}`,
    });
  }
  return chips;
}
